import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Swords, ChevronRight, ListFilter } from 'lucide-react';
import { api, type MatchSummary } from '../lib/api';
import { groupMatchesByStage, type StageMatchGroup } from '../lib/groupMatchesByStage';
import { CollapsibleSection } from './CollapsibleSection';
import { StatusBadge } from './StatusBadge';
import { HelpText } from './HelpText';
import { MatchRow } from './MatchRow';
import { PageSpinner } from './Spinner';
import { STAGE_TYPE_LABELS } from './StagePipelinePanel';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// MatchScheduleBoard — the Matches step's comp-wide schedule (TournamentSetup.tsx
// → MatchesPane). Every match in the selected competition, grouped by stage in
// pipeline order (a "Legacy / unassigned" bucket catches pre-pipeline/orphaned
// matches), sub-grouped by round wherever the stage's engine stamps one
// (groupMatchesByStage.ts carries the full per-engine breakdown + the
// isBracketStage caveat: elimination stages play out as BRACKET# slot rows,
// never a MATCH# row, so they always show zero matches here — the UI says so
// rather than rendering a silently-empty stage).
//
// Read composition is exactly the two queries the rest of the stage-pipeline
// UI already uses — ['matches', compId] (AdminPanel.tsx's MatchesSection,
// TournamentSetup's own doneMap) and ['compStages', compId]
// (StagePipelinePanel.tsx) — no new endpoint.
// ─────────────────────────────────────────────────────────────────────────────

type StatusFilter = 'ALL' | 'UNPLAYED' | 'CONFIRMED' | 'DISPUTED';

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'UNPLAYED', label: 'Unplayed' },
  { id: 'CONFIRMED', label: 'Confirmed' },
  { id: 'DISPUTED', label: 'Disputed' },
];

function matchesFilter(m: MatchSummary, filter: StatusFilter): boolean {
  switch (filter) {
    case 'ALL': return true;
    case 'CONFIRMED': return m.status === 'CONFIRMED';
    case 'DISPUTED': return m.status === 'DISPUTED';
    // Everything that hasn't reached a terminal state yet — PENDING,
    // IN_PROGRESS, AWAITING_CONFIRMATION (see shared/types.ts's MatchStatus).
    case 'UNPLAYED': return m.status !== 'CONFIRMED' && m.status !== 'DISPUTED';
  }
}

export function MatchScheduleBoard({ compId }: { compId: string }) {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const { data: matches, isLoading: matchesLoading } = useQuery({
    queryKey: ['matches', compId],
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });
  const { data: stages, isLoading: stagesLoading } = useQuery({
    queryKey: ['compStages', compId],
    queryFn: () => api.listCompStages(compId),
    enabled: !!compId,
  });

  const onResultSaved = () => {
    qc.invalidateQueries({ queryKey: ['matches', compId] });
    qc.invalidateQueries({ queryKey: ['standings', compId] });
  };

  if (matchesLoading || stagesLoading) return <PageSpinner />;

  const allMatches = matches ?? [];
  const groups = groupMatchesByStage(allMatches, stages ?? []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg bg-white/[0.03] p-1">
          <ListFilter className="ml-1.5 h-3.5 w-3.5 shrink-0 text-ink-meta" />
          {STATUS_FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                statusFilter === f.id ? 'bg-white/[0.08] text-accent-500' : 'text-ink-meta hover:text-ink-secondary',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Link to={`/admin/matches/new?compId=${encodeURIComponent(compId)}`} className="btn-ghost inline-flex items-center gap-1.5">
          <Swords className="h-4 w-4" /> Create match <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <HelpText>
        <p className="text-xs text-ink-meta">Every match in this competition, grouped by stage in pipeline order and — where the stage plays in rounds — by round underneath. Expand a stage to review its matches; expand a match to enter or correct its result inline.</p>
      </HelpText>

      {groups.length === 0 && (
        <p className="text-sm text-ink-meta">No matches scheduled yet for this competition.</p>
      )}

      <div className="space-y-3">
        {groups.map(g => (
          <StageGroupSection
            key={g.key}
            group={g}
            statusFilter={statusFilter}
            onResultSaved={onResultSaved}
          />
        ))}
      </div>
    </div>
  );
}

function StageGroupSection({
  group,
  statusFilter,
  onResultSaved,
}: {
  group: StageMatchGroup;
  statusFilter: StatusFilter;
  onResultSaved: () => void;
}) {
  const title = group.stageType ? STAGE_TYPE_LABELS[group.stageType] : 'Legacy / unassigned';
  const playedCount = group.matches.filter(m => m.status === 'CONFIRMED').length;
  const totalCount = group.matches.length;

  const rightSlot = (
    <div className="flex items-center gap-2">
      {group.stageStatus && <StatusBadge status={group.stageStatus} />}
      <span className="font-mono text-xs text-ink-meta">{playedCount}/{totalCount} played</span>
    </div>
  );

  const hint = group.stageId
    ? undefined
    : 'Matches with no stage link — created before this competition had a stage pipeline, or whose stage was since removed.';

  return (
    <CollapsibleSection
      title={title}
      hint={hint}
      right={rightSlot}
      defaultOpen={totalCount > 0}
    >
      {group.isBracketStage && (
        <p className="mb-3 text-xs text-ink-meta">
          Single/double-elimination play generates bracket slots, not match rows — this board only reads
          GET&nbsp;/matches, which never carries them. Track this stage's results on its Elimination Bracket
          panel (Setup → Competition → stage pipeline).
        </p>
      )}

      {!group.isBracketStage && totalCount === 0 && (
        <p className="text-xs text-ink-meta">No matches generated yet in this stage.</p>
      )}

      {!group.isBracketStage && totalCount > 0 && (() => {
        const visibleRounds = group.rounds
          .map(round => ({ round, visible: round.matches.filter(m => matchesFilter(m, statusFilter)) }))
          .filter(({ visible }) => visible.length > 0);

        if (visibleRounds.length === 0) {
          return <p className="text-xs text-ink-meta">No matches match the current filter.</p>;
        }
        return (
          <div className="space-y-3">
            {visibleRounds.map(({ round, visible }) => (
              <div key={round.key}>
                {round.label && (
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-meta">{round.label}</p>
                )}
                <div className="space-y-2">
                  {visible.map(m => (
                    <MatchRow key={m.sk} m={m} onResultSaved={onResultSaved} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </CollapsibleSection>
  );
}
